import React from "react";
import { Flex, Icon, Box } from "@chakra-ui/react";
import { useColorThemeToggle } from "hooks/use-color-theme-toggle";
import { type BrandColor, type ThemeBgColor } from "contexts/color-theme";
import { Logo } from "components/brand/logo";
import { Container } from "ui/container";
import { FooterCTA, type FooterCTAProps } from "./cta";
import { NavColumns } from "./nav-columns";
import { SocialAndLegalLinks } from "./social-and-legal-links";

export type FooterProps = {
  /**
   * @default "green"
   */
  variant?: BrandColor;

  /**
   * Used to fully remove the CTA section from the footer.
   * Should only be used sparingly in cases where the CTA might act as a distraction from conversion (e.g. /demo page).
   */
  hideCta?: boolean; // eslint-disable-line react/boolean-prop-naming

  /**
   * Used to pass overrides to the footer CTA component, when custom copy or actions are required.
   */
  ctaProps?: Omit<FooterCTAProps, "variant">;
};

export function Footer({ variant = "green", hideCta, ctaProps }: FooterProps) {
  const bgColor: ThemeBgColor = `new.${variant}.950`;
  const scope = useColorThemeToggle({ theme: "dark", bgColor });

  return (
    <Flex
      ref={scope}
      as="footer"
      pos="relative"
      flexDir="column"
      align="center"
      overflow="hidden"
      pt="huge"
      gap="huge"
      bg={bgColor}
    >
      {!hideCta && <FooterCTA variant={variant} {...ctaProps} />}

      <Container
        center
        pos="relative"
        zIndex="1"
        color="brand.text.dark"
        px="0"
        pb={{ base: "bigger", lg: "huge" }}
        gap="biggest"
      >
        <NavColumns />
        <Logo width={{ base: "70%", sm: "300px", md: "400px", xl: "590px" }} fill={`new.${variant}.50`} />
        <SocialAndLegalLinks />
      </Container>

      <SpotlightSvg variant={variant} />
    </Flex>
  );
}

function SpotlightSvg({ variant }: { variant: BrandColor }) {
  return (
    <Flex pos="absolute" left="0" right="0" bottom="0" justifyContent="center">
      <Icon width="2240px" height="928px" viewBox="0 0 2240 928" fill="none">
        <g clipPath="url(#footer-a)">
          <g filter="url(#footer-b)" opacity=".1">
            <Box as="ellipse" cx="1119.9" cy="1102.96" rx="799.904" ry="794.964" fill={`new.${variant}.900`} />
          </g>
          <g filter="url(#footer-c)" opacity=".2">
            <Box as="ellipse" cx="1119.93" cy="1063.17" rx="633.685" ry="629.771" fill={`new.${variant}.700`} />
          </g>
        </g>
        <defs>
          <filter
            id="footer-b"
            x="-36.093"
            y="-48.093"
            width="2312"
            height="2302.11"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
            <feGaussianBlur stdDeviation="178.047" result="effect1_foregroundBlur_293_175" />
          </filter>
          <filter
            id="footer-c"
            x="130.151"
            y="77.308"
            width="1979.56"
            height="1971.73"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
            <feGaussianBlur stdDeviation="178.047" result="effect1_foregroundBlur_293_175" />
          </filter>
          <clipPath id="footer-a">
            <path fill="#fff" d="M0 0h2240v928H0z" />
          </clipPath>
        </defs>
      </Icon>
    </Flex>
  );
}
